import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  useApproveMentor,
  useDeleteTwin,
  useTwinPersonaById,
} from '../../../../hooks/apiHooks/twinHooks'
import { resetTwinState } from '../../../../store/reducers/twinReducer'
import { setMentorId } from '../../../../store/reducers/userReducer'
import { AppDispatch } from '../../../../store/store'
import { FailedState } from './FailedState'
import { GeneratingState } from './GeneratingState'
import { PublishedSuccessState } from './PublishedSuccessState'
import { WaitingForApprovalState } from './WaitingForApprovalState'

interface PublishTabProps {
  twinId: string | null
  locationSearch: string | null
}

const PublishTab: React.FC<PublishTabProps> = memo(({ twinId }) => {
  const dispatch: AppDispatch = useDispatch()
  const location = useLocation()
  const [isPublished, setIsPublished] = useState(false)

  const { data, isLoading } = useTwinPersonaById(twinId || '', true, true)
  const { mutateAsync: deleteTwin, isPending: isDeleting } = useDeleteTwin()
  const { mutateAsync: approveMentor, isPending: isApproving } =
    useApproveMentor()

  const [searchParams] = useSearchParams()
  const isMacos = searchParams.get('platform') === 'macos'

  const mentorStatus = useMemo(
    () => data?.data?.profile?.profile?.status,
    [data],
  )

  const personaData = useMemo(() => data?.data, [data])

  const companion = {
    mentorId: twinId || '',
    avatarId: personaData?.profile?.profile?.avatarId || '',
    slug: personaData?.profile?.profile?.slug || '',
  }

  // Only show avatar when we have all required data
  const shouldShowAvatar = useMemo(() => {
    return (
      (mentorStatus === 'ReadyToUse' ||
        mentorStatus === 'WaitingForApproval') &&
      companion.avatarId &&
      companion.mentorId &&
      companion.slug
    )
  }, [mentorStatus, companion])

  useEffect(() => {
    if (
      (mentorStatus === 'ReadyToUse' ||
        mentorStatus === 'WaitingForApproval') &&
      twinId
    ) {
      dispatch(setMentorId(twinId))
    }
  }, [mentorStatus, twinId, dispatch])

  const handleRestart = async () => {
    await deleteTwin()
    dispatch(resetTwinState())
    window.location.href = `/generate-twin${location.search}`
  }

  const handlePublish = async () => {
    if (!twinId) {
      toast.error('No twin ID available')
      return
    }

    try {
      // call the approve API
      const response = await approveMentor(twinId)

      // check if response success
      if (!response?.success || !response?.approveMentor?.success) {
        console.error('Approve mentor failed:', response)
        toast.error('Failed to publish twin. Please try again.')
        return
      }

      toast.success('Twin approved and published successfully!')
      dispatch(resetTwinState())
      if (isMacos) {
        try {
          // Call the deep link for isMacOS/isIOS with mentor ID
          window.location.href = `napster-companion://cloning-success/${twinId}`
        } catch (error) {
          console.error('Failed to open deep link:', error)
        }
      }

      // Show the success screen
      setIsPublished(true)
    } catch (error) {
      console.error('Error approving mentor:', error)
      toast.error('Failed to publish twin. Please try again.')
    }
  }

  const renderCurrentState = () => {
    switch (mentorStatus) {
      case 'WaitingForApproval':
        if (isPublished) {
          return (
            <PublishedSuccessState
              twinId={twinId || ''}
              personaSlug={personaData?.profile?.profile?.slug || ''}
              isMacos={isMacos}
            />
          )
        }

        return (
          <WaitingForApprovalState
            shouldShowAvatar={!!shouldShowAvatar}
            isLoading={isLoading}
            companion={companion}
            onPublish={handlePublish}
            onRestart={handleRestart}
            isApproving={isApproving}
            isDeleting={isDeleting}
          />
        )

      case 'Failed':
        return <FailedState onRestart={handleRestart} isDeleting={isDeleting} />

      default:
        return (
          <GeneratingState
            profilePictureUrl={
              personaData?.profile?.profile?.compressedProfilePictureUrl
            }
          />
        )
    }
  }

  return <>{renderCurrentState()}</>
})

export { PublishTab }
